import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import './Game.css';
import { EmulatorContainer, TabContainer } from '../components/emulators';
import FeedbackButton from '../components/FeedbackButton';
import GameMemories from '../components/GameMemories';
import Navbar from '../components/Navbar';
import LoadingLogo from '../components/LoadingLogo';
import MapImporter from '../components/MapImporter';
import { Seo } from '../components/Seo';
import authClient from '../utils/authClient';
import {
  useDragControls,
  useMotionValue,
  useAnimate,
  motion,
} from 'framer-motion';
import useMeasure from 'react-use-measure';

const parsePreviewJson = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (_error) { return fallback; }
};

const Game = ({ previewMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Add location to get URL query params
  const [game, setGame] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [error, setError] = useState(null);
  const [gameNotFound, setGameNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [controlsData, setControlsData] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewing, setReviewing] = useState(false);
  
  // ROM versions state
  const [romVersions, setRomVersions] = useState([]);
  const [selectedRomIndex, setSelectedRomIndex] = useState(0);
  const [selectedRomPath, setSelectedRomPath] = useState(null);
  const [selectedRomData, setSelectedRomData] = useState(null);
  
  // Manual sidebar state
  const [showManualSidebar, setShowManualSidebar] = useState(false);
  const [manuals, setManuals] = useState([]);
  const [activeManualIndex, setActiveManualIndex] = useState(0);
  
  // Drag-to-close functionality for mobile modals
  const [manualScope, animateManual] = useAnimate();
  const [manualDrawerRef, { height: manualHeight }] = useMeasure();
  const manualY = useMotionValue(0);
  const manualDragControls = useDragControls();
  // eslint-disable-next-line no-unused-vars
  const isMobileView = useRef(typeof window !== 'undefined' && window.innerWidth <= 600);

  // Time tracking ref
  const timeSpentRef = useRef(0);
  const timerIntervalRef = useRef(null);

  // MPC map pre-load gate (game 939 only)
  const [mpcReady, setMpcReady] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('mpc_map_loaded') === '1';
    }
    return false;
  });

  // PWA installation state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(true);
  const swRegistrationRef = useRef(null);

  // Favorites management functions
  const getFavoritesFromStorage = () => {
    try {
      const stored = localStorage.getItem('rgm_favorite_games');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  };

  const saveFavoritesToStorage = (favoriteIds) => {
    try {
      localStorage.setItem('rgm_favorite_games', JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  };

  const [favoriteGameIds, setFavoriteGameIds] = useState(getFavoritesFromStorage);

  // eslint-disable-next-line no-unused-vars
  const toggleFavorite = useCallback((gameId) => {
    setFavoriteGameIds(prevIds => {
      const newIds = prevIds.includes(gameId) 
        ? prevIds.filter(id => id !== gameId)
        : [...prevIds, gameId];
      
      saveFavoritesToStorage(newIds);
      return newIds;
    });
  }, []);

  // eslint-disable-next-line no-unused-vars
  const isFavorite = useCallback((gameId) => {
    return favoriteGameIds.includes(gameId);
  }, [favoriteGameIds]);

  // Record game play in localStorage
  const recordGamePlay = (gameData, platformData, tags) => {
    try {
      // Get existing played games from localStorage or initialize empty array
      const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
      const playTime = new Date().toISOString();
      
      // Determine game name (prefer Chinese title if available, otherwise English)
      const name = gameData.titlechn || gameData.titleeng || 'Unknown Game';

      // Get year from game data or platform default year if null
      let year = null;
      if (gameData.year && !isNaN(parseInt(gameData.year))) {
        year = parseInt(gameData.year);
      } else if (platformData && platformData.defaultyear) {
        year = parseInt(platformData.defaultyear);
      }
      
      // Extract only tag IDs from the tags array
      const tagIds = tags.map(tag => tag.id);
      
      // Add new game play record
      playedGames.push({
        gameId: gameData.id,
        platformId: platformData.id,
        name: name,
        year: year,
        tags: tagIds,
        playTime: playTime,
        timeSpentSeconds: 0 // Initialize time spent
      });
      
      // Keep only last 50 played games
      if (playedGames.length > 50) {
        playedGames.shift();
      }
      
      // Save back to localStorage
      localStorage.setItem('playedGames', JSON.stringify(playedGames));
    } catch (error) {
      console.error('Error recording game play:', error);
    }
  };

  // Update time spent in localStorage
  const updateTimeSpent = () => {
    try {
      const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
      
      // Find the current game in the played games array
      const currentGameIndex = playedGames.findIndex(game => game.gameId === parseInt(id));
      
      if (currentGameIndex !== -1) {
        // Update the time spent for this game
        playedGames[currentGameIndex].timeSpentSeconds = timeSpentRef.current;
        localStorage.setItem('playedGames', JSON.stringify(playedGames));
      }
    } catch (error) {
      console.error('Error updating time spent:', error);
    }
  };

  // Setup time tracking
  useEffect(() => {
    if (previewMode) return undefined;
    // Start the timer when component mounts
    timerIntervalRef.current = setInterval(() => {
      if (!document.hidden) {
        timeSpentRef.current += 1;
        updateTimeSpent();
        console.log('Time spent on game:', timeSpentRef.current, 'seconds');
      }
    }, 1000);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Tab inactive, pausing timer');
      } else {
        console.log('Tab active, resuming timer');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      clearInterval(timerIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      console.log('Final time spent:', timeSpentRef.current, 'seconds');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, previewMode]);

  // Prevent arrow keys from scrolling the page
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Prevent default scrolling behavior for arrow keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Get available ROM versions
  const processRomData = (gameData) => {
    // Check if ROM field looks like JSON (starts with [ or {)
    if (gameData.rom && typeof gameData.rom === 'string' && 
        (gameData.rom.trim().startsWith('[') || gameData.rom.trim().startsWith('{'))) {
      try {
        // Try to parse ROM field as JSON
        const romData = JSON.parse(gameData.rom);
        
        if (Array.isArray(romData) && romData.length > 0) {
          // New JSON format
          return romData.map((rom, index) => ({
            id: `version-${index}`,
            index: index,
            name: rom.type || `Version ${index + 1}`,
            path: rom.path,
            data: rom // Keep all rom data for potential emulator-specific info
          }));
        }
      } catch (e) {
        console.error('Error parsing ROM JSON, using legacy format:', e);
      }
    }
    
    // Legacy format - construct versions from individual fields
    const versions = [];
    
    if (gameData.rom) {
      versions.push({
        id: 'default',
        index: 0,
        name: '默认',
        path: gameData.rom,
        data: { type: '默认', path: gameData.rom }
      });
    }
    
    if (gameData.jpnrom) {
      versions.push({
        id: 'japanese',
        index: 1,
        name: '日版',
        path: gameData.jpnrom,
        data: { type: '日版', path: gameData.jpnrom }
      });
    }
    
    if (gameData.chnrom) {
      versions.push({
        id: 'chinese',
        index: 2,
        name: '简中版',
        path: gameData.chnrom,
        data: { type: '简中版', path: gameData.chnrom }
      });
    }
    
    return versions;
  };
  
  useEffect(() => {
    if (!previewMode && parseInt(id) === 939) {
      const queryParams = new URLSearchParams(location.search);
      if (!queryParams.get('version')) {
        const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const targetVersion = isMobile ? '移动版' : '电脑版';
        navigate(`/game/939?version=${encodeURIComponent(targetVersion)}`, { replace: true });
        return;
      }
    }
    if (!previewMode && parseInt(id) === 939 && !mpcReady) return;
    const fetchGame = async () => {
      try {
        setLoading(true);
        setError(null);
        setGameNotFound(false);
        if (previewMode && !authClient.isAuthenticated()) {
          navigate('/login', { state: { from: { pathname: `/manage/pending-game/${id}/play` } } });
          return;
        }
        const gameRes = await apiClient.get(previewMode ? `/visitor-game-submissions/${id}` : `/games/${id}`);
        if (!previewMode && (!gameRes.data || !gameRes.data.id)) {
          setGameNotFound(true);
          setError('未找到该游戏。');
          return;
        }
        let gameData = gameRes.data;
        if (previewMode) {
          const token = encodeURIComponent(gameData.preview_token);
          const filePath = (file) => `/visitor-game-submissions/${id}/preview-files/${encodeURIComponent(file.filename)}?token=${token}`;
          const pendingRoms = parsePreviewJson(gameData.rom_files, []).map((file, index) => ({
            type: file.typeLabel || file.type || `版本${index + 1}`,
            path: filePath(file)
          }));
          const pendingManuals = parsePreviewJson(gameData.manual_files, []).map((file, index) => ({
            name: file.name || `手册${index + 1}`,
            path: filePath(file)
          }));
          gameData = {
            ...gameData,
            id: Number(id),
            rom: JSON.stringify(pendingRoms),
            manual: JSON.stringify(pendingManuals),
            cover: null
          };
        }
        setGame(gameData);
        
        // Process manuals
        let processedManuals = [];
        if (gameData.manual) {
          try {
            const parsedManuals = JSON.parse(gameData.manual);
            if (Array.isArray(parsedManuals) && parsedManuals.length > 0) {
              processedManuals = parsedManuals;
            } else if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
              processedManuals = [{ name: 'Manual', path: gameData.manual }];
            }
          } catch (e) {
            if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
              processedManuals = [{ name: 'Manual', path: gameData.manual }];
            }
          }
        }
        setManuals(processedManuals);
        setActiveManualIndex(0);
        
        // Process ROM versions
        const availableVersions = processRomData(gameData);
        setRomVersions(availableVersions);
        
        // Get version from URL query param or localStorage
        const queryParams = new URLSearchParams(location.search);
        const versionFromUrl = queryParams.get('version');
        const versionFromLocalStorage = localStorage.getItem('selectedRomVersion');
        
        // Select an initial version - try URL param first, then localStorage, then stored index
        let initialIndex = 0; // Default to first ROM
        
        if (versionFromUrl && availableVersions.length > 0) {
          // Find version by type from URL param - try various matching approaches
          const matchedVersion = availableVersions.findIndex(v => 
            v.name === versionFromUrl || 
            v.data.type === versionFromUrl ||
            v.name.toLowerCase() === versionFromUrl.toLowerCase() ||
            v.data.type.toLowerCase() === versionFromUrl.toLowerCase()
          );
          
          if (matchedVersion !== -1) {
            initialIndex = matchedVersion;
            console.log(`Found ROM version match for '${versionFromUrl}' at index ${matchedVersion}`);
          } else {
            console.log(`No ROM version found matching '${versionFromUrl}', using default index 0`);
          }
        } else if (versionFromLocalStorage && availableVersions.length > 0) {
          // Find version by type from localStorage - try various matching approaches
          const matchedVersion = availableVersions.findIndex(v => 
            v.name === versionFromLocalStorage || 
            v.data.type === versionFromLocalStorage ||
            v.name.toLowerCase() === versionFromLocalStorage.toLowerCase() ||
            v.data.type.toLowerCase() === versionFromLocalStorage.toLowerCase()
          );
          
          if (matchedVersion !== -1) {
            initialIndex = matchedVersion;
            console.log(`Found ROM version match for '${versionFromLocalStorage}' from localStorage at index ${matchedVersion}`);
          } else {
            console.log(`No ROM version found matching '${versionFromLocalStorage}' from localStorage, using default index 0`);
          }
        } else {
          // Use stored preference if valid
          const storedVersion = localStorage.getItem('selectedRomIndex');
          if (storedVersion !== null) {
            const parsedIndex = parseInt(storedVersion, 10);
            if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < availableVersions.length) {
              initialIndex = parsedIndex;
            }
          }
        }
        
        // Set selected version and path
        if (availableVersions.length > 0) {
          setSelectedRomIndex(initialIndex);
          setSelectedRomPath(availableVersions[initialIndex].path);
          setSelectedRomData(availableVersions[initialIndex].data);
          localStorage.setItem('selectedRomIndex', initialIndex.toString());
        }
        
        const platformsRes = await apiClient.get('/platforms');
        const platform = platformsRes.data.find(p => p.id === parseInt(gameData.platform));
        setPlatform(platform);

        // Load controls data from database
        if (platform && platform.ctrljson) {
          let controlsData = platform.ctrljson;
          
          // Resolve credit IDs to full credit objects
          if (controlsData.creditIds && Array.isArray(controlsData.creditIds) && controlsData.creditIds.length > 0) {
            try {
              // Fetch all credits for these IDs
              const creditPromises = controlsData.creditIds.map(id => 
                apiClient.get(`/credits/${id}`).catch(err => {
                  console.error(`Error fetching credit ${id}:`, err);
                  return null;
                })
              );
              
              const creditResponses = await Promise.all(creditPromises);
              const resolvedCredits = creditResponses
                .filter(res => res !== null)
                .map(res => res.data);
              
              // Replace empty credits array with resolved credits
              controlsData = {
                ...controlsData,
                credits: resolvedCredits
              };
            } catch (err) {
              console.error('Error resolving credit IDs:', err);
            }
          }
          
          setControlsData(controlsData);
          console.log('Controls JSON loaded from database');
        } else {
          setControlsData(getDefaultControlsData());
        }

        // Fetch tags and record game play
        const tags = previewMode ? [] : (await apiClient.get(`/games/${id}/tags`)).data || [];
        if (previewMode) return;

        recordGamePlay(gameData, platform, tags);
        
        // Check if we should increment view count based on playedGames
        const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
        const currentGameEntry = playedGames.find(game => game.gameId === parseInt(id));
        
        // Only increment view if the game wasn't played recently (within the last 30 seconds)
        const wasPlayedRecently = currentGameEntry && 
          currentGameEntry.timeSpentSeconds && 
          currentGameEntry.timeSpentSeconds < 30;
        
        if (!wasPlayedRecently) {
          try {
            const response = await apiClient.post(`/games/${id}/view`);
            if (response.data.incremented) {
              console.log('View count incremented for game:', id);
            } else {
              console.log('View not counted by backend:', response.data.message);
            }
          } catch (viewErr) {
            console.error('Error handling view count:', viewErr);
          }
        } else {
          console.log('Game played recently (under 30 seconds), not incrementing view count');
        }

        // Debug info only in console
        console.log('Game Debug Info:', {
          gameId: id,
          platformCode: gameData.platform,
          platformEmulator: platform?.emulator,
          platformEmulatorOptions: platform?.emulatoroptions,
          platformControlsJson: platform?.ctrljson,
          romVersions: availableVersions,
          selectedVersion: availableVersions[initialIndex],
          backendUrl: API_BASE_URL,
          frontendUrl: 'https://rgm.games',
          ejsDataUrl: 'https://rgm.games/ejsdata'
        });
      } catch (err) {
        console.error('Error fetching game:', err);
        if (!previewMode && err.response?.status === 404) {
          setGameNotFound(true);
          setError('未找到该游戏。');
        } else {
          setError('无法载入游戏，请稍后重试。');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id, location.search, mpcReady, navigate, previewMode]);

  // PWA Installation Logic
  useEffect(() => {
    if (!game || previewMode) return;

    // Check if already installed in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      setShowInstallBanner(false);
      return;
    }

    // Check if user dismissed banner in this session
    const wasDismissed = sessionStorage.getItem(`pwa-dismissed-${id}`);
    if (wasDismissed) {
      setShowInstallBanner(false);
      return;
    }

    // Update manifest link dynamically
    const updateManifest = () => {
      let manifestLink = document.querySelector('link[rel="manifest"]');
      
      if (!manifestLink) {
        manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        document.head.appendChild(manifestLink);
      }
      
      // Use same-origin proxied manifest URL
      manifestLink.href = `/game-manifest-${id}.json`;
      console.log(`Manifest link updated to: ${manifestLink.href}`);
    };

    updateManifest();
    console.log(`PWA setup for game ${id} - Manifest: /game-manifest-${id}.json`);

    // Register game-specific service worker with game-specific scope
    const registerServiceWorker = async () => {
      if ('serviceWorker' in navigator) {
        try {
          // Unregister any existing service workers for other games first
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            // Only unregister game-specific service workers, not the main site SW
            if (registration.active && registration.active.scriptURL.includes('/game-sw-')) {
              const currentGameId = registration.active.scriptURL.match(/\/game-sw-(\d+)\.js/)?.[1];
              if (currentGameId && currentGameId !== id) {
                console.log(`Unregistering service worker for game ${currentGameId}`);
                await registration.unregister();
              }
            }
          }
          
          // Register game-specific service worker with game-specific scope
          // Service worker must be served from same origin, so we use a local path
          // that will be proxied or served by the frontend server
          const swUrl = `/game-sw-${id}.js`;
          
          // IMPORTANT: Scope must match the page URL structure
          // Using '/game/{id}' (no trailing slash) to match the actual page URL
          const registration = await navigator.serviceWorker.register(swUrl, {
            scope: `/game/${id}`
          });
          
          swRegistrationRef.current = registration;
          console.log(`Game ${id} PWA Service Worker registered:`, registration);
          console.log(`Service Worker URL: ${swUrl}`);
          console.log(`Service Worker Scope: /game/${id}`);
          
          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;
          
        } catch (error) {
          console.error('Service Worker registration failed:', error);
        }
      }
    };

    registerServiceWorker();

    // Listen for beforeinstallprompt event (capture it if available)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('Install prompt available');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('Game PWA was installed');
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Show banner after a short delay
    const timer = setTimeout(() => {
      setShowInstallBanner(true);
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [game, id, previewMode]);

  // Handle PWA installation
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Use native install prompt if available
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to install prompt: ${outcome}`);
        
        if (outcome === 'accepted') {
          setShowInstallBanner(false);
        }
        setDeferredPrompt(null);
      } catch (err) {
        console.error('Install prompt error:', err);
      }
    } else {
      // Show manual installation instructions
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      let message = '';
      if (isIOS) {
        message = '在 Safari 中:\n1. 点击底部分享按钮\n2. 选择"添加到主屏幕"\n3. 点击"添加"';
      } else if (isMobile) {
        message = '在 Chrome 中:\n1. 点击右上角菜单 (⋮)\n2. 选择"添加到主屏幕"\n3. 点击"添加"';
      } else {
        message = '安装失败，请确认浏览器不处于隐私模式，并刷新重试。';
      }
      
      alert(message);
    }
  };

  // Dismiss install banner
  // eslint-disable-next-line no-unused-vars
  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    // Remember dismissal for this session
    sessionStorage.setItem(`pwa-dismissed-${id}`, 'true');
  };
  
  // Handle manual modal close with animation
  const handleManualClose = async () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 600) {
      // Mobile: use drag animation
      animateManual(manualScope.current, {
        opacity: [1, 0],
      });

      const yStart = typeof manualY.get() === 'number' ? manualY.get() : 0;

      await animateManual('#manual-drawer', {
        y: [yStart, manualHeight || 400],
      });
    }
    setShowManualSidebar(false);
  };
  
  // Toggle manual sidebar
  const toggleManualSidebar = () => {
    setShowManualSidebar(!showManualSidebar);
  };
  
  // Handle manual tab change
  const handleManualChange = (index) => {
    setActiveManualIndex(index);
  };

  const handlePreviewReview = async (action) => {
    const label = action === 'approve' ? '批准并发布' : '拒绝';
    if (!window.confirm(`确定要${label}这个访客提交吗？`)) return;
    setReviewing(true);
    try {
      const response = await apiClient.post(`/visitor-game-submissions/${id}/${action}`, { reviewComment });
      navigate(action === 'approve' ? `/game-details/${response.data.gameId}` : '/manage');
    } catch (reviewError) {
      setError(reviewError.message || `${label}失败。`);
      setReviewing(false);
    }
  };
  
  useEffect(() => {
    if (romVersions.length > 0 && selectedRomIndex >= 0 && selectedRomIndex < romVersions.length) {
      const selectedVersion = romVersions[selectedRomIndex];
      if (selectedVersion) {
        setSelectedRomPath(selectedVersion.path);
        setSelectedRomData(selectedVersion.data);
        localStorage.setItem('selectedRomIndex', selectedRomIndex.toString());
      }
    }
  }, [selectedRomIndex, romVersions]);

  // Helper function to generate default controls data
  const getDefaultControlsData = () => {
    return {
      controls: {
        player1: [
          { key: '方向键', action: '方向' },
          { key: 'X', action: '确认 / A' },
          { key: 'Z', action: '取消 / B' },
          { key: 'A', action: 'L' },
          { key: 'S', action: 'R' },
          { key: 'Q', action: 'SELECT' },
          { key: 'W', action: 'START' }
        ],
        player2: [
          { key: 'I/J/K/L', action: '方向' },
          { key: '1', action: '确认 / A' },
          { key: '2', action: '取消 / B' },
          { key: '5', action: 'L' },
          { key: '6', action: 'R' },
          { key: '8', action: 'SELECT' },
          { key: '9', action: 'START' }
        ]
      },
      notices: [
        { type: 'hint', text: '提示：键位可能因游戏而异，部分游戏可能需要特殊按键。' }
      ],
      credits: []
    };
  };

  if (error) {
    return (
      <div className="game-wrapper">
        <Navbar />
        <div className="game-container">
          <div className="game-header">
            <button onClick={() => navigate('/games')} className="back-button">
              <span className="back-icon">←</span>返回
            </button>
          </div>
          <div className="error-message">{error}</div>
          {gameNotFound && (
            <p className="game-not-found-help">
              找不到游戏了吗？可以在<Link to="/games">游戏库</Link>中搜索。
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!previewMode && parseInt(id) === 939 && !mpcReady) {
    return (
      <div className="game-wrapper">
        <Navbar />
        <MapImporter gameId={id} onComplete={() => setMpcReady(true)} />
      </div>
    );
  }

  if (loading || !game || !platform) {
    return (
      <div className="game-wrapper">
        <Navbar />
        <div className="game-container">
          <div className="game-header">
            <button onClick={() => navigate('/games')} className="back-button">
              <span className="back-icon">←</span>返回
            </button>
          </div>
          <div className="game-loading-container">
            <LoadingLogo />
            <div className="loading-text">正在准备游戏中...</div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have multiple ROM versions
  const hasMultipleVersions = romVersions.length > 1;

  // Create a modified game object with the correct ROM data for the emulator
  const gameWithSelectedRom = {
    ...game,
    // Replace the rom property with the selected ROM path to ensure compatibility
    rom: selectedRomPath,
    // Add activeRom property for new emulator components
    activeRom: selectedRomPath,
    activeRomData: selectedRomData,
    activeRomIndex: selectedRomIndex
  };

  const isDesktop = typeof window !== 'undefined' && window.innerWidth > 600;
  const primaryTitle = game.titlechn || game.titleeng || '经典游戏';
  const secondaryTitle = game.titlechn && game.titleeng ? game.titleeng : '';
  const platformName = platform.namechn || platform.nameeng || '经典游戏平台';
  const cleanDescription = (game.desc || '').replace(/\s+/g, ' ').trim();
  const seoTitleSuffix = ` - ${platformName} | 怀旧游戏博物馆`;
  const seoTitleWithEnglish = `${primaryTitle}在线玩${secondaryTitle ? `（${secondaryTitle}）` : ''}${seoTitleSuffix}`;
  const seoTitle = [...seoTitleWithEnglish].length <= 64
    ? seoTitleWithEnglish
    : `${primaryTitle}在线玩${seoTitleSuffix}`;
  const seoDescription = [
    `${primaryTitle}${secondaryTitle ? `（${secondaryTitle}）` : ''}是怀旧游戏博物馆收录的${platformName}平台经典游戏。`,
    game.year ? `发行于${game.year}年。` : '',
    game.dev ? `开发者为${game.dev}。` : '',
    game.publisher ? `发行商为${game.publisher}。` : '',
    `馆藏编号 RGM-${game.id}，可在电脑或手机浏览器中免费在线体验。`,
    cleanDescription,
  ].filter(Boolean).join('').slice(0, 180);
  const canonicalUrl = previewMode
    ? `https://rgm.games/manage/pending-game/${id}/play`
    : `https://rgm.games/game/${game.id}`;
  const coverUrl = game.cover
    ? (/^https?:\/\//i.test(game.cover) ? game.cover : `https://rgm.games/api${game.cover.startsWith('/') ? game.cover : `/${game.cover}`}`)
    : 'https://rgm.games/icon512.png';

  return (
    <div className="game-wrapper">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        image={coverUrl}
        type="video.game"
        keywords={`${primaryTitle}在线玩,${primaryTitle}游戏,${secondaryTitle ? `${secondaryTitle} online,` : ''}${platformName}游戏,经典游戏在线玩`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'VideoGame',
            '@id': `${canonicalUrl}#game`,
            identifier: `RGM-${game.id}`,
            name: primaryTitle,
            alternateName: secondaryTitle || undefined,
            description: cleanDescription || seoDescription,
            image: coverUrl,
            url: canonicalUrl,
            inLanguage: 'zh-CN',
            datePublished: game.year ? String(game.year) : undefined,
            gamePlatform: [platform.namechn, platform.nameeng].filter(Boolean),
            publisher: game.publisher ? { '@type': 'Organization', name: game.publisher } : undefined,
            author: game.dev ? { '@type': 'Organization', name: game.dev } : undefined,
            playMode: game.multiplayer ? 'MultiPlayer' : 'SinglePlayer',
            isAccessibleForFree: true,
            potentialAction: {
              '@type': 'PlayAction',
              target: canonicalUrl,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '怀旧游戏博物馆', item: 'https://rgm.games/' },
              { '@type': 'ListItem', position: 2, name: '游戏库', item: 'https://rgm.games/games' },
              { '@type': 'ListItem', position: 3, name: platformName, item: `https://rgm.games/games/platform/${platform.id}` },
              { '@type': 'ListItem', position: 4, name: primaryTitle, item: canonicalUrl },
            ],
          },
        ]}
        robots={previewMode ? 'noindex, nofollow, noarchive' : undefined}
      />
      <Navbar />
      {previewMode && (
        <div className="pending-play-preview-bar">
          <div><strong>待审核游戏 · 游玩测试</strong><span>这是实际游戏页面和模拟器，仅管理员可访问；不会被收录或列入游戏库。</span></div>
          <textarea value={reviewComment} onChange={(event) => setReviewComment(event.target.value)} maxLength="2000" placeholder="馆员审核留言（通过或拒绝后贡献者可见）" />
          <button type="button" className="preview-details" onClick={() => navigate(`/manage/pending-game/${id}`)}>返回详情</button>
          <button type="button" className="preview-reject" disabled={reviewing} onClick={() => handlePreviewReview('reject')}>拒绝</button>
          <button type="button" className="preview-approve" disabled={reviewing} onClick={() => handlePreviewReview('approve')}>批准并发布</button>
        </div>
      )}
      
      {/* Manual sidebar button - Side Tab (PC only) */}
      {isDesktop && manuals.length > 0 && (
        <button 
          type="button"
          className="game-manual-button"
          onClick={toggleManualSidebar}
        >
          查看手册
        </button>
      )}
      
      <div className="game-container">
        {/* Header with back button and game title */}
        <div className="game-header">
          <button onClick={() => navigate('/games')} className="back-button">
            <span className="back-icon">←</span>返回
          </button>
          
          {/* Install PWA button - PC only */}
          {!previewMode && isDesktop && showInstallBanner && (
            <button onClick={handleInstallClick} className="install-pwa-button">
              <svg className="install-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              安装
            </button>
          )}

          {/* Reinstall MPC map button - game 939 only */}
          {!previewMode && parseInt(id) === 939 && mpcReady && (
            <div className="reinstall-mpc-wrapper">
              <MapImporter gameId={id} onComplete={() => {}} showReinstall />
            </div>
          )}

          {/* More games button - game 939 only, PC header */}
          {!previewMode && parseInt(id) === 939 && (
            <button className="more-games-button more-games-button--header" onClick={() => navigate('/feed')}>
              <svg className="more-games-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M738.538 292 285.478 292C163.082 292 64 382.916 64 510.836 64 638.838 163.082 732 285.478 732l453.058 0C860.98 732 960 638.838 960 510.836 960 382.916 860.98 292 738.538 292zM400 533.394c0 5.396-4.842 10.606-10.476 10.606L320 544l0 70.278c0 5.66-6.208 9.722-11.606 9.722l-42.854 0c-5.256 0-9.542-3.836-9.542-9.066L255.998 544 185.722 544c-5.66 0-9.722-6.208-9.722-11.606l0-42.854c0-5.256 3.836-9.542 9.066-9.542L256 479.998l0-69.644c0-5.612 3.708-10.356 9.106-10.356l44.29 0c5.396 0 10.606 4.842 10.606 10.476L320.002 480l69.644 0c5.612 0 10.356 3.708 10.356 9.106L400.002 533.394zM639.532 550.972c-21.472 0-38.902-17.174-38.902-38.306 0-21.13 17.43-38.336 38.902-38.336 21.566 0 38.996 17.206 38.996 38.336C678.528 533.798 661.098 550.972 639.532 550.972zM724.628 634.602c-21.472 0-38.902-17.058-38.902-38.168 0-21.278 17.43-38.306 38.902-38.306 21.566 0 38.996 17.03 38.996 38.306C763.624 617.542 746.196 634.602 724.628 634.602zM724.628 467.23c-21.472 0-38.902-17.158-38.902-38.292 0-21.134 17.43-38.338 38.902-38.338 21.566 0 38.996 17.204 38.996 38.338C763.624 450.072 746.196 467.23 724.628 467.23zM809.724 550.972c-21.458 0-38.884-17.174-38.884-38.306 0-21.13 17.428-38.336 38.884-38.336 21.474 0 38.904 17.206 38.904 38.336C848.628 533.798 831.2 550.972 809.724 550.972z"/>
              </svg>
              游玩更多游戏...
            </button>
          )}
          <div className="game-title">
            {game.titleeng ? (
              <h1 className="game-title-eng" style={{textAlign: 'center'}}>{game.titleeng}</h1>
            ) : game.titlechn ? (
              <h1 className="game-title-chn" style={{textAlign: 'center'}}>{game.titlechn}</h1>
            ) : (
              <h1 className="game-title-eng" style={{textAlign: 'center'}}>Untitled Game</h1>
            )}
            {game.titlechn && game.titleeng && <h2 className="game-title-chn" style={{textAlign: 'center'}}>{game.titlechn}</h2>}
          </div>
          
          {/* ROM Version Switcher - display only if multiple ROM versions are available */}
          {hasMultipleVersions && (
            <div className="version-switcher">
              <div className="version-tabs">
                {romVersions.map((version, index) => (
                  <button
                    key={version.id}
                    className={`version-tab ${selectedRomIndex === index ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedRomIndex(index);
                    }}
                  >
                    {version.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="game-content">
          {/* Controls and Credits Tab Container */}
          <TabContainer 
            platform={platform} 
            controlsData={controlsData}
            gameId={String(game.id)}
            pageContext="game"
            additionalHint={game.additional_hint}
          />

          {/* Emulator Container */}
          <EmulatorContainer
            game={gameWithSelectedRom}
            platform={platform}
            selectedRomVersion={romVersions[selectedRomIndex]?.id || 'default'}
            romVersions={romVersions}
            pageContext="game"
          />

          {/* Reinstall MPC map button below emulator - mobile only */}
          {!previewMode && parseInt(id) === 939 && mpcReady && (
            <div className="reinstall-mpc-below">
              <MapImporter gameId={id} onComplete={() => {}} showReinstall />
            </div>
          )}

          {/* More games button below emulator - game 939, mobile only */}
          {!previewMode && parseInt(id) === 939 && (
            <div className="more-games-below">
              <button className="more-games-button" onClick={() => navigate('/feed')}>
                <svg className="more-games-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                  <path d="M738.538 292 285.478 292C163.082 292 64 382.916 64 510.836 64 638.838 163.082 732 285.478 732l453.058 0C860.98 732 960 638.838 960 510.836 960 382.916 860.98 292 738.538 292zM400 533.394c0 5.396-4.842 10.606-10.476 10.606L320 544l0 70.278c0 5.66-6.208 9.722-11.606 9.722l-42.854 0c-5.256 0-9.542-3.836-9.542-9.066L255.998 544 185.722 544c-5.66 0-9.722-6.208-9.722-11.606l0-42.854c0-5.256 3.836-9.542 9.066-9.542L256 479.998l0-69.644c0-5.612 3.708-10.356 9.106-10.356l44.29 0c5.396 0 10.606 4.842 10.606 10.476L320.002 480l69.644 0c5.612 0 10.356 3.708 10.356 9.106L400.002 533.394zM639.532 550.972c-21.472 0-38.902-17.174-38.902-38.306 0-21.13 17.43-38.336 38.902-38.336 21.566 0 38.996 17.206 38.996 38.336C678.528 533.798 661.098 550.972 639.532 550.972zM724.628 634.602c-21.472 0-38.902-17.058-38.902-38.168 0-21.278 17.43-38.306 38.902-38.306 21.566 0 38.996 17.03 38.996 38.306C763.624 617.542 746.196 634.602 724.628 634.602zM724.628 467.23c-21.472 0-38.902-17.158-38.902-38.292 0-21.134 17.43-38.338 38.902-38.338 21.566 0 38.996 17.204 38.996 38.338C763.624 450.072 746.196 467.23 724.628 467.23zM809.724 550.972c-21.458 0-38.884-17.174-38.884-38.306 0-21.13 17.428-38.336 38.884-38.336 21.474 0 38.904 17.206 38.904 38.336C848.628 533.798 831.2 550.972 809.724 550.972z"/>
                </svg>
                游玩更多游戏...
              </button>
            </div>
          )}

        </div>

        {!previewMode && <GameMemories gameId={game.id} gameName={game.titlechn || game.titleeng} />}

        {/* Feedback button to report game issues */}
        {!previewMode && <FeedbackButton
          type="unplayable" 
          gameId={game.id} 
          gameName={game.titleeng || game.titlechn} 
          position="bottom-right"
        />}
      </div>
      
      {/* Manual sidebar */}
      {showManualSidebar && (
        <motion.div
          ref={manualScope}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={handleManualClose}
          className="game-manual-sidebar-overlay"
        >
          <motion.div
            id="manual-drawer"
            ref={manualDrawerRef}
            onClick={(e) => e.stopPropagation()}
            initial={typeof window !== 'undefined' && window.innerWidth <= 600 ? { y: '100%' } : { x: '100%' }}
            animate={typeof window !== 'undefined' && window.innerWidth <= 600 ? { y: '0%' } : { x: '0%' }}
            transition={{
              ease: 'easeInOut',
            }}
            className="game-manual-sidebar open"
            style={typeof window !== 'undefined' && window.innerWidth <= 600 ? { y: manualY } : {}}
            drag={typeof window !== 'undefined' && window.innerWidth <= 600 ? 'y' : false}
            dragControls={manualDragControls}
            onDragEnd={() => {
              if (manualY.get() >= 100) {
                handleManualClose();
              }
            }}
            dragListener={false}
            dragConstraints={{
              top: 0,
              bottom: 0,
            }}
            dragElastic={{
              top: 0,
              bottom: 0.5,
            }}
          >
            <div className="drag-handle-container">
              <button
                onPointerDown={(e) => {
                  if (typeof window !== 'undefined' && window.innerWidth <= 600) {
                    manualDragControls.start(e);
                  }
                }}
                className="close-sidebar-button drag-handle"
                onClick={handleManualClose}
              >
                {typeof window !== 'undefined' && window.innerWidth > 600 && (
                  <span className="close-icon">×</span>
                )}
              </button>
            </div>
            
            <div className="manual-sidebar-content">
              <h3 className="manual-sidebar-title">游戏手册</h3>
              
              {manuals.length > 0 ? (
                <div className="manual-section">
                  {/* Manual tab selector */}
                  {manuals.length > 1 && (
                    <div className="manual-selector">
                      <div className="game-manual-tabs">
                        {manuals.map((manual, index) => (
                          <button
                            key={index}
                            className={`game-manual-tab ${index === activeManualIndex ? 'active' : ''}`}
                            onClick={() => handleManualChange(index)}
                          >
                            {manual.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Manual display */}
                  <div className="manual-container">
                    <iframe 
                      src={`/flipbook.html?pdf=${API_BASE_URL}${manuals[activeManualIndex].path}`}
                      title={manuals[activeManualIndex].name} 
                      className="manual-frame"
                      allowFullScreen={true}
                    />
                  </div>
                </div>
              ) : (
                <div className="no-manual">
                  <p className="no-content">暂无游戏手册</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Game;
